// Task #3822 — sezioni del dettaglio progetto: membri, knowledge, skill.
// Componenti coesi e piccoli, montati da ProjectDetailPanel.
import React, { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Plus, Trash2, UserMinus, UserPlus, Wrench, X } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import type { PaProjectKnowledgeDoc, PaProjectMember, PaProjectSkill, TenantUserSearchResult } from '@easyflow/shared';
import { projectsApi } from './useProjects';

// ---------------------------------------------------------------------------
// Membri
// ---------------------------------------------------------------------------
interface MembersSectionProps {
  projectId: string;
  members: PaProjectMember[];
  isOwner: boolean;
  currentUserId: string | null;
  onChanged: () => void;
  /** Uscita dal progetto da parte del membro corrente (chiude il dettaglio). */
  onLeft: () => void;
}

export const MembersSection: React.FC<MembersSectionProps> = ({
  projectId, members, isOwner, currentUserId, onChanged, onLeft,
}) => {
  const [inviting, setInviting] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TenantUserSearchResult[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!inviting) return;
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      projectsApi.searchTenantUsers(query)
        .then(setResults)
        .catch((err) => {
          console.error('[MembersSection] user search failed', err);
          setResults([]);
        });
    }, 250);
    return () => { if (searchTimer.current) window.clearTimeout(searchTimer.current); };
  }, [inviting, query]);

  const memberIds = new Set(members.map((m) => m.userId));

  const handleInvite = async (userId: string) => {
    setBusy(userId);
    setError(null);
    try {
      const ok = await projectsApi.addMember(projectId, { userId, role: 'member' });
      if (ok) { setInviting(false); setQuery(''); onChanged(); }
      else setError('Invito non riuscito');
    } catch (err) {
      console.error('[MembersSection] invite failed', err);
      setError('Invito non riuscito');
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (userId: string, isSelf: boolean) => {
    setBusy(userId);
    setError(null);
    try {
      const ok = await projectsApi.removeMember(projectId, userId);
      if (ok) { if (isSelf) onLeft(); else onChanged(); }
      else setError('Rimozione non riuscita');
    } catch (err) {
      console.error('[MembersSection] remove failed', err);
      setError('Rimozione non riuscita');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between px-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Membri ({members.length})
        </span>
        {isOwner && (
          <Button
            variant="ghost" size="sm" className="h-5 w-5 p-0"
            onClick={() => { setInviting(v => !v); setError(null); }}
            title="Invita un collega" aria-label="Invita un collega"
          >
            {inviting ? <X className="h-3 w-3" /> : <UserPlus className="h-3 w-3" />}
          </Button>
        )}
      </div>
      {inviting && (
        <div className="px-1 py-1 space-y-1">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca collega per nome o email"
            autoFocus
            className="w-full h-6 px-2 rounded border border-border bg-background text-[11px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-violet-400"
          />
          <ul className="max-h-32 overflow-y-auto">
            {results.filter(u => !memberIds.has(u.id)).map(u => (
              <li key={u.id}>
                <button
                  type="button"
                  disabled={busy === u.id}
                  onClick={() => void handleInvite(u.id)}
                  className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded text-[11px] text-left hover:bg-muted disabled:opacity-60"
                >
                  {busy === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 text-violet-500" />}
                  <span className="flex-1 min-w-0 truncate">{u.first_name} {u.last_name}</span>
                  <span className="text-[9px] text-muted-foreground truncate max-w-[90px]">{u.email ?? ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <div className="text-[10px] text-red-600 px-1">{error}</div>}
      <ul className="mt-0.5">
        {members.map((m) => {
          const isSelf = m.userId === currentUserId;
          const name = [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email || m.userId;
          const canRemove = m.role !== 'owner' && (isOwner || isSelf);
          return (
            <li key={m.id} className="flex items-center gap-1.5 px-1.5 py-1 rounded text-[11px] hover:bg-muted group">
              <span className="flex-1 min-w-0 truncate" title={m.email ?? undefined}>
                {name}{isSelf ? ' (tu)' : ''}
              </span>
              <span className="text-[9px] uppercase text-muted-foreground">{m.role === 'owner' ? 'propr.' : 'membro'}</span>
              {canRemove && (
                <button
                  type="button"
                  disabled={busy === m.userId}
                  onClick={() => void handleRemove(m.userId, isSelf)}
                  title={isSelf ? 'Esci dal progetto' : 'Rimuovi membro'}
                  aria-label={isSelf ? 'Esci dal progetto' : 'Rimuovi membro'}
                  className="opacity-0 group-hover:opacity-100 h-5 w-5 rounded inline-flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 disabled:opacity-60"
                >
                  {busy === m.userId ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserMinus className="h-3 w-3" />}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Knowledge (documenti → pipeline KB esistente sull'agente nascosto)
// ---------------------------------------------------------------------------
interface KnowledgeSectionProps {
  projectId: string;
  knowledgeAgentId: string | null;
  docs: PaProjectKnowledgeDoc[];
  isOwner: boolean;
  onChanged: () => void;
}

export const KnowledgeSection: React.FC<KnowledgeSectionProps> = ({
  projectId, knowledgeAgentId, docs, isOwner, onChanged,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busyDocId, setBusyDocId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const agentId = knowledgeAgentId ?? await projectsApi.ensureKnowledgeAgent(projectId);
      if (!agentId) { setError('Impossibile preparare la knowledge del progetto'); return; }
      const ok = await projectsApi.uploadKnowledgeDoc(agentId, file);
      if (ok) onChanged();
      else setError('Caricamento non riuscito');
    } catch (err) {
      console.error('[KnowledgeSection] upload failed', err);
      setError('Caricamento non riuscito');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId: string) => {
    if (!knowledgeAgentId) return;
    setBusyDocId(docId);
    setError(null);
    try {
      const ok = await projectsApi.deleteKnowledgeDoc(knowledgeAgentId, docId);
      if (ok) onChanged();
      else setError('Eliminazione non riuscita');
    } catch (err) {
      console.error('[KnowledgeSection] delete failed', err);
      setError('Eliminazione non riuscita');
    } finally {
      setBusyDocId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between px-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Conoscenza ({docs.length})
        </span>
        {isOwner && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleUpload(f);
              }}
            />
            <Button
              variant="ghost" size="sm" className="h-5 w-5 p-0"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              title="Carica documento" aria-label="Carica documento"
            >
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            </Button>
          </>
        )}
      </div>
      {error && <div className="text-[10px] text-red-600 px-1">{error}</div>}
      <ul className="mt-0.5">
        {docs.length === 0 && (
          <li className="text-[10px] text-muted-foreground px-1.5 py-1">
            Nessun documento: le chat del progetto usano solo la knowledge personale.
          </li>
        )}
        {docs.map((d) => (
          <li key={d.id} className="flex items-center gap-1.5 px-1.5 py-1 rounded text-[11px] hover:bg-muted group">
            <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="flex-1 min-w-0 truncate" title={d.filename}>{d.filename}</span>
            <span className="text-[9px] text-muted-foreground shrink-0">
              {d.status === 'completed' ? `${d.chunkCount} chunk` : d.status}
            </span>
            {isOwner && (
              <button
                type="button"
                disabled={busyDocId === d.id}
                onClick={() => void handleDelete(d.id)}
                title="Elimina documento" aria-label="Elimina documento"
                className="opacity-0 group-hover:opacity-100 h-5 w-5 rounded inline-flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                {busyDocId === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skill (contratto #3821: junction verso claude_skills, cap 8)
// ---------------------------------------------------------------------------
interface SkillsSectionProps {
  projectId: string;
  skills: PaProjectSkill[];
  isOwner: boolean;
  onChanged: () => void;
}

export const SkillsSection: React.FC<SkillsSectionProps> = ({
  projectId, skills, isOwner, onChanged,
}) => {
  const [picking, setPicking] = useState(false);
  const [catalog, setCatalog] = useState<PaProjectSkill[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!picking) return;
    setLoadingCatalog(true);
    projectsApi.skillsCatalog()
      .then(setCatalog)
      .catch((err) => {
        console.error('[SkillsSection] catalog load failed', err);
        setError('Impossibile caricare il catalogo skill');
      })
      .finally(() => setLoadingCatalog(false));
  }, [picking]);

  const attached = new Set(skills.map((s) => s.skillId));

  const toggleSkill = async (skillId: string) => {
    if (saving) return;
    const next = attached.has(skillId)
      ? skills.filter((s) => s.skillId !== skillId).map((s) => s.skillId)
      : [...skills.map((s) => s.skillId), skillId];
    if (next.length > 8) { setError('Massimo 8 skill per progetto'); return; }
    setSaving(true);
    setError(null);
    try {
      const ok = await projectsApi.setSkills(projectId, next);
      if (ok) onChanged();
      else setError('Salvataggio skill non riuscito');
    } catch (err) {
      console.error('[SkillsSection] set skills failed', err);
      setError('Salvataggio skill non riuscito');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between px-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Skill ({skills.length}/8)
        </span>
        {isOwner && (
          <Button
            variant="ghost" size="sm" className="h-5 w-5 p-0"
            onClick={() => { setPicking(v => !v); setError(null); }}
            title="Gestisci skill" aria-label="Gestisci skill"
          >
            {picking ? <X className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
          </Button>
        )}
      </div>
      {error && <div className="text-[10px] text-red-600 px-1">{error}</div>}
      {!picking && (
        <ul className="mt-0.5">
          {skills.length === 0 && (
            <li className="text-[10px] text-muted-foreground px-1.5 py-1">Nessuna skill agganciata.</li>
          )}
          {skills.map((s) => (
            <li key={s.skillId} className="flex items-center gap-1.5 px-1.5 py-1 text-[11px]">
              <Wrench className="h-3 w-3 shrink-0 text-violet-500" />
              <span className="flex-1 min-w-0 truncate" title={s.description ?? undefined}>{s.displayTitle}</span>
              {s.latestVersion && <span className="text-[9px] text-muted-foreground">v{s.latestVersion}</span>}
            </li>
          ))}
        </ul>
      )}
      {picking && (
        <ul className="mt-0.5 max-h-40 overflow-y-auto">
          {loadingCatalog && (
            <li className="flex justify-center py-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /></li>
          )}
          {!loadingCatalog && catalog.length === 0 && (
            <li className="text-[10px] text-muted-foreground px-1.5 py-1">Nessuna skill disponibile nel tenant.</li>
          )}
          {catalog.map((s) => {
            const isOn = attached.has(s.skillId);
            return (
              <li key={s.skillId}>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void toggleSkill(s.skillId)}
                  className={`w-full flex items-center gap-1.5 px-1.5 py-1 rounded text-[11px] text-left hover:bg-muted disabled:opacity-60 ${isOn ? 'text-violet-700' : 'text-foreground'}`}
                  title={s.description ?? undefined}
                >
                  <span className={`h-3 w-3 shrink-0 rounded-sm border inline-flex items-center justify-center ${isOn ? 'bg-violet-600 border-violet-600' : 'border-border'}`}>
                    {isOn && <span className="h-1.5 w-1.5 bg-white rounded-[1px]" />}
                  </span>
                  <span className="flex-1 min-w-0 truncate">{s.displayTitle}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
